import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, X, Check } from "@phosphor-icons/react";
import StatusPill from "@/components/StatusPill";

const STATUS_OPTS = ["activo", "prospecto", "inactivo"];

const emptyForm = { name: "", email: "", phone: "", company: "", status: "prospecto", notes: "" };

export default function Clientes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/clients").then((r) => setItems(r.data.clients || []))
      .catch(() => toast.error("No se pudieron cargar los clientes"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const { data } = await api.put(`/clients/${editingId}`, form);
        setItems((its) => its.map((x) => (x.id === editingId ? data : x)));
        toast.success("Cliente actualizado");
      } else {
        const { data } = await api.post("/clients", form);
        setItems((its) => [data, ...its]);
        toast.success("Cliente agregado");
      }
      setShowForm(false); setForm(emptyForm); setEditingId(null);
    } catch { toast.error("No se pudo guardar"); }
  };

  const edit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, email: c.email || "", phone: c.phone || "", company: c.company || "", status: c.status, notes: c.notes || "" });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    try {
      await api.delete(`/clients/${id}`);
      setItems((its) => its.filter((x) => x.id !== id));
      toast.success("Eliminado");
    } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div data-testid="page-clientes">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono text-muted-foreground">Clientes</div>
          <h1 className="display text-5xl md:text-6xl mt-1">Tu cartera</h1>
        </div>
        <button
          onClick={() => { setShowForm(true); setForm(emptyForm); setEditingId(null); }}
          className="btn-primary flex items-center gap-2"
          data-testid="btn-new-client"
        >
          <Plus size={14} weight="bold" /> Nuevo cliente
        </button>
      </header>

      {showForm && (
        <form onSubmit={submit} className="brut-card mb-8 space-y-4" data-testid="client-form">
          <div className="flex items-center justify-between">
            <div className="label-mono text-muted-foreground">{editingId ? "Editar cliente" : "Nuevo cliente"}</div>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm); }} className="btn-ghost"><X size={16} weight="bold" /></button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <div className="label-mono mb-1">Nombre</div>
              <input required className="input-brut" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name" />
            </label>
            <label className="block">
              <div className="label-mono mb-1">Empresa</div>
              <input className="input-brut" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="client-company" />
            </label>
            <label className="block">
              <div className="label-mono mb-1">Correo</div>
              <input type="email" className="input-brut" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" />
            </label>
            <label className="block">
              <div className="label-mono mb-1">Teléfono</div>
              <input className="input-brut" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" />
            </label>
            <label className="block">
              <div className="label-mono mb-1">Estado</div>
              <select className="input-brut" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="client-status">
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2">
              <div className="label-mono mb-1">Notas</div>
              <textarea className="input-brut min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes" />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex items-center gap-2" data-testid="btn-save-client">
              <Check size={14} weight="bold" /> Guardar
            </button>
          </div>
        </form>
      )}

      {loading && <div className="text-sm text-muted-foreground">Cargando…</div>}
      {!loading && items.length === 0 && (
        <div className="brut-card text-sm text-muted-foreground">Todavía no hay clientes registrados.</div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className="brut-card" data-testid={`client-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="display text-xl">{c.name}</div>
                {c.company && <div className="text-xs text-muted-foreground mt-0.5">{c.company}</div>}
              </div>
              <StatusPill value={c.status} />
            </div>
            <div className="mt-3 text-sm space-y-1">
              {c.email && <div><span className="label-mono text-muted-foreground">Correo:</span> {c.email}</div>}
              {c.phone && <div><span className="label-mono text-muted-foreground">Teléfono:</span> {c.phone}</div>}
              {c.notes && <div className="mt-2 border-t-2 border-foreground pt-2 text-muted-foreground">{c.notes}</div>}
            </div>
            <div className="mt-3 flex gap-2">
              <button onClick={() => edit(c)} className="btn-ghost flex items-center gap-1" data-testid={`btn-edit-client-${c.id}`}>
                <PencilSimple size={14} weight="bold" /> Editar
              </button>
              <button onClick={() => remove(c.id)} className="btn-ghost flex items-center gap-1" data-testid={`btn-remove-client-${c.id}`}>
                <Trash size={14} weight="bold" /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
